export type QueuedStudent = {
  studentId: string;
  name: string;
  joinTime: number;
  lastHeartbeat: number;
};

/**
 * Earliest first. The studentId tie-break matters: two students joining in
 * the same millisecond must order identically on every device, or the muqri
 * and the student disagree about who is next.
 */
export function orderQueue(students: QueuedStudent[]): QueuedStudent[] {
  return [...students].sort(
    (a, b) => a.joinTime - b.joinTime || a.studentId.localeCompare(b.studentId)
  );
}

export function positionOf(students: QueuedStudent[], studentId: string): number | null {
  const idx = orderQueue(students).findIndex((s) => s.studentId === studentId);
  return idx === -1 ? null : idx + 1;
}

export const HEARTBEAT_INTERVAL_MS = 5000;

/**
 * DEFERRED — the same 15 000 ms is written again as STALE_THRESHOLD_MS in
 * functions/waitingCount.js, in a different runtime with nothing linking the
 * two. Change one and the discovery badge and the معلم's queue start
 * disagreeing about the same student.
 */
export const STALE_THRESHOLD_MS = 15000;

/**
 * A student is present only while their app keeps beating. Nothing depends on
 * a clean exit: a killed or backgrounded app simply stops, ages out, and
 * leaves the queue on its own.
 *
 * DEFERRED — three unsynchronised clocks. `lastHeartbeat` is now written by
 * the SERVER (serverTimestamp), but `now` here is whichever device is asking:
 * the student's, for their own position, and the معلم's, for the queue they
 * are admitting from. The sweeper's clock is a third. A device running two
 * minutes fast sees a present student as stale; one running slow keeps a dead
 * one. Reconciling them needs a server-offset estimate applied at every read
 * site, which is not done here.
 */
export function isStale(student: QueuedStudent, now: number): boolean {
  return now - student.lastHeartbeat > STALE_THRESHOLD_MS;
}

export function activeStudents(students: QueuedStudent[], now: number): QueuedStudent[] {
  return orderQueue(students.filter((s) => !isStale(s, now)));
}

/**
 * The much longer silence after which the sweeper actually DELETES a queue
 * document (functions/waitingCount.js REAP_THRESHOLD_MS). Staleness and
 * removal are two different questions and they are not the same number on
 * purpose: a backgrounded app should stop inflating the badge within seconds
 * without also losing the place in the queue it has been holding for ten
 * minutes.
 *
 * DEFERRED — the same duplication across two runtimes as STALE_THRESHOLD_MS
 * above: this literal is written again in functions/waitingCount.js with
 * nothing linking them.
 */
export const REAP_THRESHOLD_MS = 120000;

/** True once the sweeper would have removed this student from the queue. */
export function isReaped(student: QueuedStudent, now: number): boolean {
  return now - student.lastHeartbeat > REAP_THRESHOLD_MS;
}

/**
 * Everyone still IN the queue — which is a different question from who is
 * currently present. A student is told they were dropped, and offered a
 * rejoin that resets their joinTime and their place, only when this says they
 * are gone. Judging that by the 15 s staleness threshold meant a phone
 * backgrounded for twenty seconds announced that the connection had dropped,
 * and a student who believed it went to the back of a queue they were still
 * near the front of.
 */
export function queuedStudents(students: QueuedStudent[], now: number): QueuedStudent[] {
  return orderQueue(students.filter((s) => !isReaped(s, now)));
}
